import { create } from 'zustand';

/**
 * What the editor's save confirmed (SceneView's saveScene).
 * - `saved`: the backend accepted the scene, and the floors and architecture
 *   GLBs it points at are the ones just written.
 * - `savedIncomplete`: the scene record was stored, but with the previous GLBs
 *   or floors (an upload or the floors PUT failed) — the Navigator would show
 *   an outdated room.
 * - `failed`: nothing usable was stored.
 * - `cancelled`: the user dismissed the first-save name prompt.
 * - `unchanged`: nothing was saved because nothing had changed — what the
 *   server holds is already the scene as shown, so the popup opens as well.
 */
export type SaveOutcome = 'saved' | 'savedIncomplete' | 'failed' | 'cancelled' | 'unchanged';

export interface LiveViewSaveOptions {
  /** Save even with no unsaved edits — for a scene whose server copy is known
   *  to be incomplete (a configurator hand-off has no architecture GLBs yet). */
  forceSave?: boolean;
}

type SaveForLiveView = (options?: LiveViewSaveOptions) => Promise<SaveOutcome>;

interface LiveViewStore {
  /**
   * idle → saving → starting (only once the server holds the current scene)
   * → open → idle. `starting`: the streaming session is being started or
   * reused while the dialog stays shut and the button spins; LiveViewModal
   * opens it once the Navigator is loading the scene.
   */
  status: 'idle' | 'saving' | 'starting' | 'open';
  /**
   * Registered by SceneView while the editor is mounted: the button lives in
   * the header, outside the editor, but the save it has to run lives inside.
   * Null whenever no editor is open — the button hides then.
   */
  saveForLiveView: SaveForLiveView | null;
  register: (save: SaveForLiveView) => void;
  /** Clears the registration only if it is still `save` — a remount may already have replaced it. */
  unregister: (save: SaveForLiveView) => void;
  /** Saves if there is anything to save, and opens the popup only when the
   *  server holds the current scene (save confirmed, or nothing to save). */
  requestLiveView: (options?: LiveViewSaveOptions) => Promise<SaveOutcome | null>;
  /**
   * The dialog was minimized and its session left running — the header
   * button shows a live dot while that session is still up. Cleared by any
   * reopen, a close, or the editor going away; a session that ends on its own
   * while minimized is caught by the button (it reads the streaming state).
   */
  minimized: boolean;
  /** starting → open; a no-op from any other state (closed or unregistered meanwhile). */
  markOpen: () => void;
  minimize: () => void;
  close: () => void;
}

export const useLiveViewStore = create<LiveViewStore>((set, get) => ({
  status: 'idle',
  minimized: false,
  saveForLiveView: null,
  register: (save) => set({ saveForLiveView: save }),
  unregister: (save) => {
    if (get().saveForLiveView === save) set({ saveForLiveView: null, status: 'idle', minimized: false });
  },
  requestLiveView: async (options) => {
    const { status, saveForLiveView } = get();
    if (status !== 'idle' || !saveForLiveView) return null;
    set({ status: 'saving', minimized: false });
    let outcome: SaveOutcome = 'failed';
    try {
      outcome = await saveForLiveView(options);
    } catch (err) {
      console.warn('[liveView] save failed:', err);
    }
    // The editor may have gone away while the save ran (unregister reset us).
    if (get().status === 'saving') {
      set({ status: outcome === 'saved' || outcome === 'unchanged' ? 'starting' : 'idle' });
    }
    return outcome;
  },
  markOpen: () => {
    if (get().status === 'starting') set({ status: 'open' });
  },
  minimize: () => set({ status: 'idle', minimized: true }),
  close: () => set({ status: 'idle', minimized: false }),
}));
